import styled from "@emotion/styled";
import { Money } from "@shopify/hydrogen-react";
import type { ProductPriceRange } from "@shopify/hydrogen-react/storefront-api-types";
import {
  AbsoluteLink,
  BlockTag,
  FlexRow,
  ScreenReaderText,
  Stack,
  Text,
} from "atoms";
import { preventForwarding } from "styles/styleUtils";
import { paths } from "utils/paths";
import type { CollectionQueryProductData } from "../api/useCollectionQuery";
import { isOnSale } from "../utils/isOnSale";
import { ProductImage } from "./ProductImage";
import { ProductTag } from "./ProductTag";

type ProductCardProps = {
  product: CollectionQueryProductData;
  artistName?: string;
};

function ProductCard({
  product: {
    artistId,
    availableForSale,
    compareAtPriceRange,
    featuredImage,
    handle,
    priceRange,
    productType,
    title,
  },
  artistName,
}: ProductCardProps) {
  const productIsOnSale = isOnSale({ compareAtPriceRange, priceRange });

  return (
    <Card>
      {artistId && (
        <AbsoluteLink to={paths.artistShopProduct(artistId, handle)}>
          <ScreenReaderText>
            {title}
            {!availableForSale && <span>, Sold out</span>}
          </ScreenReaderText>
        </AbsoluteLink>
      )}

      {featuredImage && (
        <CardImage
          data={featuredImage}
          aspectRatio="1"
          sizes="(max-width: 420px) 100vw, (max-width: 740px) 50vw, (max-width: 1050px) 30vw,  (max-width: 1440px) 25vw, 300px"
        />
      )}

      <CardBody>
        <Stack gap="xxs">
          <CardTitle aria-hidden>
            {title}

            {(!availableForSale || productIsOnSale) && (
              <CardTagsTopRight>
                {productIsOnSale && (
                  <BlockTag bg="error" color="bg" size="xs">
                    Sale
                  </BlockTag>
                )}

                {!availableForSale && (
                  <BlockTag bg="error" color="bg" size="xs">
                    Sold out
                  </BlockTag>
                )}
              </CardTagsTopRight>
            )}
          </CardTitle>
          {artistName && <ArtistName>by {artistName}</ArtistName>}
        </Stack>

        <FlexRow
          justifyContent="space-between"
          gap="0 2px"
          flexWrap="nowrap"
          itemsFlex="0 1 auto"
          alignItems="flex-end"
        >
          <Price>
            <ProductCardPrice
              priceRange={priceRange}
              compareAtPriceRange={compareAtPriceRange}
              productIsOnSale={productIsOnSale}
            />
          </Price>

          <ProductTag type={productType} size="xxs" />
        </FlexRow>
      </CardBody>
    </Card>
  );
}

const Card = styled.li(({ theme }) => ({
  borderRadius: theme.borderRadius.sm,
  boxShadow: theme.boxShadow.light,
  display: "flex",
  flexDirection: "column",
  backgroundColor: theme.colors.bg,
  padding: 0,
  position: "relative",
  margin: 0,
  "&::marker": {
    visibility: "hidden",
    position: "absolute",
    opacity: 0,
  },
}));

const CardImage = styled(ProductImage)(({ theme }) => ({
  border: 0,
  borderRadius: `${theme.borderRadius.sm} ${theme.borderRadius.sm} 0 0`,
  display: "block",
  objectFit: "cover",
}));

const CardBody = styled.div({
  display: "flex",
  gap: 10,
  flexDirection: "column",
  justifyContent: "space-between",
  flex: 1,
  overflow: "hidden",
  paddingInline: "4%",
  paddingBlock: "4%",
});

const CardTitle = styled(Text)(({ theme }) => ({
  ...theme.fonts.display,
  color: theme.colors.fg,
  fontSize: theme.fontSizes.sm,
  lineHeight: 1,
  display: "-webkit-box",
  lineClamp: "2",
  margin: 0,
  overflow: "hidden",
  WebkitLineClamp: "2",
  WebkitBoxOrient: "vertical",
  textOverflow: "ellipsis",
}));

const ArtistName = styled(Text)({
  fontSize: 12,
  margin: 0,
  lineHeight: 1,
});

const Price = styled.div({
  fontSize: 16,
});

const CardTagsTopRight = styled(FlexRow)({
  position: "absolute",
  gap: 5,
  top: 10,
  right: 10,
});

type ProductCardPriceProps = {
  priceRange: ProductPriceRange;
  compareAtPriceRange: ProductPriceRange;
  productIsOnSale: boolean;
};

function ProductCardPrice({
  priceRange,
  compareAtPriceRange,
  productIsOnSale,
}: ProductCardPriceProps) {
  const minPrice = priceRange.minVariantPrice.amount;
  const maxPrice = priceRange.maxVariantPrice.amount;
  const hasPriceRange = maxPrice !== minPrice;

  return (
    <ProductPrice>
      <FlexRow gap="0 3px">
        {hasPriceRange && <small>from</small>}
        <SaleMoney
          data={priceRange.minVariantPrice}
          productIsOnSale={productIsOnSale}
          withoutTrailingZeros
        />
      </FlexRow>

      {productIsOnSale && (
        <>
          <ScreenReaderText>marked down from</ScreenReaderText>
          <StrikeMoney
            data={compareAtPriceRange.minVariantPrice}
            withoutTrailingZeros
          />
        </>
      )}
    </ProductPrice>
  );
}

const ProductPrice = styled.div(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  lineHeight: 1,
  flexWrap: "wrap",
  color: theme.colors.fg,
  gap: "0 4px",
}));

const StrikeMoney = styled(Money)(({ theme }) => ({
  fontSize: ".85em",
  color: theme.colors.fg60,
  textDecoration: "line-through",
}));

const SaleMoney = styled(Money, {
  shouldForwardProp: preventForwarding("productIsOnSale"),
})<{ productIsOnSale: boolean }>(({ productIsOnSale = false, theme }) => ({
  color: productIsOnSale ? theme.colors.error : theme.colors.fg,
  fontSize: 15,
}));

export { ProductCard, SaleMoney, StrikeMoney };
